import classNames from 'classnames';
import { Badge, type BadgeProps } from 'components/atoms/Badge';
import useWorldTheme from 'hooks/shared/useWorldTheme';
import { motion } from 'motion/react';
import type { ComponentProps, FC } from 'react';
import ReactMarkdown from 'react-markdown';
import type { color } from 'types/shared/worlds';
import type SVGType from 'types/svg.types';
import { fadeTransition } from 'utils/constants';

const themes = {
  taxes: {
    iconWrapper:
      'w-10 h-10 p-1.5 rounded-full bg-taxes-100 text-taxes-900 stroke-taxes-900 fill-taxes-900',
    icon: 'text-taxes',
  },
  bank: {
    iconWrapper:
      'w-10 h-10 p-1.5 rounded-full bg-bank-100 text-bank-900 stroke-bank-900 fill-bank-900',
    icon: 'text-bank',
  },
  expenses: {
    iconWrapper:
      'w-10 h-10 p-1.5 rounded-full bg-expenses-100 text-expenses-900 stroke-expenses-900 fill-expenses-900',
    icon: 'text-expenses',
  },
  primary: {
    iconWrapper:
      'w-10 h-10 p-1.5 rounded-full bg-primary-100 text-primary-900 stroke-primary-900 fill-primary-900',
    icon: 'text-primary',
  },
  revenues: {
    iconWrapper:
      'w-10 h-10 p-1.5 rounded-full bg-revenues-100 text-revenues-900 stroke-revenues-900 fill-revenues-900',
    icon: 'text-revenues',
  },
  red: {
    iconWrapper:
      'w-10 h-10 p-1.5 rounded-full bg-red-100 text-red-500 stroke-red-500 fill-red-500',
    icon: 'text-red-500',
  },
  yellow: {
    iconWrapper:
      'w-10 h-10 p-1.5 rounded-full bg-yellow-100 text-yellow-500 stroke-yellow-500 fill-yellow-500',
    icon: 'text-yellow-500',
  },
  green: {
    iconWrapper:
      'w-10 h-10 p-1.5 rounded-full bg-green-100 text-green-500 stroke-green-500 fill-green-500',
    icon: 'text-green-500',
  },
};

const ModalHeading: FC<{
  Icon: FC<ComponentProps<'div'>> | SVGType;
  title: string;
  subtitle?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  iconClassName?: string;
  iconWrapper?: boolean;
  color?: color | 'red' | 'yellow' | 'green';
  titleBadge?: BadgeProps;
}> = ({
  Icon,
  title,
  subtitle,
  titleClassName,
  subtitleClassName,
  iconClassName,
  iconWrapper = true,
  color,
  titleBadge,
}) => {
  const worldColor = useWorldTheme();

  const _theme = themes[(color || worldColor) as keyof typeof themes];

  return (
    <>
      <div className="flex items-center justify-center w-full">
        <motion.div
          {...fadeTransition}
          className={classNames(
            iconWrapper ? _theme.iconWrapper : '',
            'flex items-center justify-center',
          )}
        >
          <Icon className={classNames(_theme.icon, 'w-7 h-7', iconClassName)} />
        </motion.div>
      </div>

      <motion.div {...fadeTransition} className="my-4 text-center">
        {titleBadge ? (
          <div className="flex items-center justify-center mb-2">
            <Badge
              size={titleBadge.size}
              color={titleBadge.color}
              icon={titleBadge.icon}
            >
              {titleBadge.children}
            </Badge>
          </div>
        ) : null}

        <h1
          className={classNames(
            'text-2xl font-bold text-primary-800 mx-auto',
            titleClassName,
          )}
          style={{ hyphens: 'none' }}
        >
          {title}
        </h1>

        {subtitle ? (
          <ReactMarkdown
            components={{
              p: ({ children }) => (
                <p
                  className={classNames(
                    'text-primary-700 mt-2 mx-auto',
                    subtitleClassName,
                  )}
                >
                  {children}
                </p>
              ),
            }}
          >
            {subtitle}
          </ReactMarkdown>
        ) : null}
      </motion.div>
    </>
  );
};

export default ModalHeading;
